import React, { useEffect, useMemo } from 'react';

interface JoinCircleSuccessProps {
  circleName: string;
  hasPromo: boolean;
  promoMessage: string | null;
  circleId: string | null;
  inviteToken: string | null;
}

type Platform = 'ios' | 'android' | 'other';

const APP_STORE_URL = 'https://apps.apple.com/us/app/kizu-mobile/id6757609621';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.knox.mediavault';

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

function getMobileWebUrl(): string {
  if (typeof window === 'undefined') return 'https://mobile.kizu.online';
  const host = window.location.host;
  if (host.includes('dev.')) return 'https://dev.mobile.kizu.online';
  return 'https://mobile.kizu.online';
}

function buildAppDeepLink(circleId: string | null, inviteToken: string | null): string | null {
  // Prefer the circle deep link — if the user already has a session, the
  // app routes them straight into the circle with no auth step. Fall back
  // to join-circle/<token> which the app's linking config also handles.
  if (circleId) return `kizu://circle/${circleId}`;
  if (inviteToken) return `kizu://join-circle/${inviteToken}`;
  return null;
}

export const JoinCircleSuccess: React.FC<JoinCircleSuccessProps> = ({
  circleName,
  hasPromo,
  promoMessage,
  circleId,
  inviteToken,
}) => {
  const platform = useMemo(detectPlatform, []);
  const mobileWebUrl = useMemo(getMobileWebUrl, []);
  const deepLink = useMemo(() => buildAppDeepLink(circleId, inviteToken), [circleId, inviteToken]);

  // Try to bounce the recipient straight into the installed Kizu app on
  // mount. If they already have a session, this lands them inside the
  // circle with NO additional sign-in / OTP. If they don't have the app
  // installed (or aren't signed in), iOS/Android silently swallow the
  // unhandled custom-scheme link and they stay on this page, where the
  // download CTAs below are still visible.
  useEffect(() => {
    if (!deepLink) return;
    if (platform === 'other') return; // desktop browsers can't open kizu://
    // Use a hidden iframe on iOS to avoid the "page can't be opened" alert
    // that some Safari versions show for unhandled custom schemes.
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = deepLink;
      document.body.appendChild(iframe);
      // Clean up after a moment so we don't leave the iframe lying around.
      const cleanup = window.setTimeout(() => {
        try { document.body.removeChild(iframe); } catch { /* noop */ }
      }, 2000);
      return () => window.clearTimeout(cleanup);
    } catch {
      // If iframe injection fails, fall through silently — the manual
      // "Open in Kizu app" button below still works.
    }
  }, [deepLink, platform]);

  return (
    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
      <img src="/kizu-mascot.png" alt="Kizu" className="w-16 h-16 mx-auto mb-3" />
      <h1 className="text-2xl font-bold text-gray-800 mb-2">Welcome to {circleName}!</h1>
      <p className="text-gray-600 mb-6">
        You're in. Open Kizu to view and share photos with everyone in this circle.
      </p>

      {hasPromo && promoMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <p className="text-green-800 font-medium text-sm">{promoMessage}</p>
          <p className="text-green-600 text-xs mt-1">
            This will be automatically applied when you subscribe.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {/* Primary CTA: open the installed app via deep link. If the app is
            installed and the user is already signed in, this drops them
            straight into the circle — no OTP, no extra steps. */}
        {deepLink && platform !== 'other' && (
          <a
            href={deepLink}
            className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
          >
            Open in Kizu App
          </a>
        )}

        {/* Platform-aware download CTA — for users who don't have the app yet. */}
        {platform === 'ios' && (
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-black text-white font-semibold py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Download Kizu for iPhone
          </a>
        )}
        {platform === 'android' && (
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-black text-white font-semibold py-3 px-4 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Download Kizu for Android
          </a>
        )}
        {platform === 'other' && (
          <div className="flex gap-3 items-center justify-center">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <img
                src="https://www.kizu.online/app-store-badge.png"
                alt="Download on the App Store"
                height="44"
              />
            </a>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
              <img
                src="https://www.kizu.online/google-play-badge.png"
                alt="Get it on Google Play"
                height="44"
              />
            </a>
          </div>
        )}

        {/* Secondary CTA: stay in the browser via the mobile web app. */}
        <a
          href={mobileWebUrl}
          className="block w-full bg-white border-2 border-blue-600 text-blue-600 font-semibold py-3 px-4 rounded-lg hover:bg-blue-50 transition-colors"
        >
          Continue in Browser
        </a>
      </div>

      <p className="text-xs text-gray-400 mt-6">
        Already signed into the app? Tap "Open in Kizu App" above — you'll go straight to {circleName}.
      </p>
    </div>
  );
};
